/**
 * SSO 회원가입 페이지
 * 
 * @description
 * KHA 등 외부 SSO 제공자에서 리다이렉트된 사용자에게
 * 정보 제공 동의를 받고 회원가입 폼으로 이동
 * 
 * @route /sso/:origin/signup/:userSsoAccessId
 * 
 * @example
 * /sso/kha/signup/24
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSsoInfo } from '@/services/ssoService';
import { AlertDialog } from '@/components/shared/ui';
import SsoConsentForm from '@/components/sso/SsoConsentForm';
import { ROUTES } from '@/lib/constants/routes';
import type { SsoOrigin, UserSsoAccessDto } from '@/types/api/sso';

export default function SsoSignup(): React.ReactElement {
  const { origin, userSsoAccessId } = useParams<{
    origin: SsoOrigin;
    userSsoAccessId: string;
  }>();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [userSsoAccess, setUserSsoAccess] = useState<UserSsoAccessDto | null>(null);
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    message: string;
  }>({
    isOpen: false,
    message: '',
  });

  const showAlert = (message: string) => {
    setAlertState({ isOpen: true, message });
  };

  const closeAlert = () => {
    setAlertState({ isOpen: false, message: '' });
  };

  useEffect(() => {
    const fetchSsoInfo = async () => {
      // URL 파라미터 검증
      if (!origin || origin !== 'kha') {
        showAlert('지원하지 않는 SSO 제공자입니다.');
        setIsLoading(false);
        return;
      }

      if (!userSsoAccessId || isNaN(Number(userSsoAccessId))) {
        showAlert('잘못된 SSO 접근 정보입니다.');
        setIsLoading(false);
        return;
      }

      const userSsoAccessIdNum = Number(userSsoAccessId);

      try {
        // UserSsoAccess 정보 조회
        const ssoInfo = await getSsoInfo(userSsoAccessIdNum);
        setUserSsoAccess(ssoInfo.userSsoAccess);
        setIsLoading(false);
      } catch (error: any) {
        showAlert(error.message || 'SSO 정보를 불러올 수 없습니다.');
        setIsLoading(false);
      }
    };

    fetchSsoInfo();
  }, [origin, userSsoAccessId]);

  const handleConsent = () => {
    // TODO: 회원가입 폼으로 이동 (다음 스텝에서 구현)
    navigate(`/sso/${origin}/signup/${userSsoAccessId}/form`);
  };

  const handleCancel = () => {
    // 홈으로 이동
    navigate(ROUTES.HOME);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent mb-4"></div>
          <p className="text-gray-600 text-lg">정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!userSsoAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <AlertDialog isOpen={alertState.isOpen} onClose={closeAlert} message={alertState.message} />
      </div>
    );
  }

  return (
    <>
      <SsoConsentForm
        origin={origin as SsoOrigin}
        userSsoAccess={userSsoAccess}
        onConsent={handleConsent}
        onCancel={handleCancel}
      />
      <AlertDialog isOpen={alertState.isOpen} onClose={closeAlert} message={alertState.message} />
    </>
  );
}
